/**
 * Surface-агностичное ядро редактора одной записи времени (задача 77 §1.1).
 *
 * Типы вынесены из `NewEntryFlow.tsx`. Ядро (шаги `вид → проект → элемент →
 * часы`, draft, pills, валидация) не знает про конкретный API — мутации и финал
 * инкапсулирует `EntryEditorAdapter`. Шаг «элемент сборки» (задача 89 §4.2)
 * зависит от проекта (адресный список карточки проекта) — поэтому идёт после
 * него; элемент необязателен, payload шлёт `assembly_element_id: null` при
 * пропуске.
 *
 * Две реализации адаптера:
 *   - `useSelfFieldAdapter` — worker-self (field-API + offline-очередь + 409);
 *   - `useAdminEntryAdapter` — внесение за сотрудника (admin-API, без offline).
 */
import type {
  Project,
  TimesheetEntry,
  TimesheetEntryPayload,
  WorkType,
} from "@/api/field-timesheet";
import type { FormValidationIssue } from "@/components/ui/form-validation";

export type Step = 1 | 2 | 3 | 4;

/**
 * Поверхность редактора (задача 77 §2.1). Рулит ТОЛЬКО контейнером, density и
 * свапом ручного ввода № проекта — шаги/логика/состав записи общие.
 *   - `mobile` — fullscreen `createPortal` + `NumericPad` (путь рабочего `/field`);
 *   - `desktop-dock` — правый док 460px (`<Sheet side=right>`) + компактная
 *     density + ручной № проекта обычным `<input>` (внесение за сотрудника).
 */
export type EntrySurface = "mobile" | "desktop-dock";

export interface Draft {
  // Step 1
  workTypeId: number | null;
  workTypeText: string | null;
  // Step 2
  projectId: number | null;
  projectCodeRaw: string | null;
  /** §20/2.1 — true когда пользователь явно нажал «Без проекта»; отличает
   * «не выбрано» от «осознанно пропустил». buildPayload → оба поля NULL. */
  projectSkipped: boolean;
  // Step 3 — элемент сборки (задача 89 §4.2), зависит от выбранного проекта.
  assemblyElementId: number | null;
  /** true когда пользователь явно нажал «Без элемента» на непустом списке;
   * отличает «осознанно пропустил» от «не выбрано». buildPayload → NULL. */
  elementSkipped: boolean;
  // Step 4
  hoursHalf: number | null;
  /** §115/2.1 — опциональный идентификатор доработки («напр.: замечание ОТК»).
   * Живёт per выбранная пара (проект, вид работ); сбрасывается при смене пары.
   * `null`/пустая строка → обычная запись без идентификатора. */
  reworkNote: string | null;
}

export const EMPTY_DRAFT: Draft = {
  workTypeId: null,
  workTypeText: null,
  projectId: null,
  projectCodeRaw: null,
  projectSkipped: false,
  assemblyElementId: null,
  elementSkipped: false,
  hoursHalf: null,
  reworkNote: null,
};

export type Action =
  | { type: "selectWorkType"; id: number }
  | { type: "selectWorkTypeText"; text: string }
  | { type: "selectProjectId"; projectId: number }
  | { type: "selectProjectRaw"; code: string }
  | { type: "skipProject" }
  | { type: "selectElement"; id: number }
  | { type: "skipElement" }
  | { type: "setHours"; half: number }
  | { type: "setReworkNote"; note: string }
  | { type: "resetFromStep"; step: Step }
  | { type: "goBack" }
  | { type: "prefill"; draft: Draft; step: Step };

export interface State {
  step: Step;
  draft: Draft;
}

export interface PillLabels {
  step1: string | null;
  step2: string | null;
  step3: string | null;
}

/**
 * Surface-агностичный контракт мутаций per-record редактора.
 *
 * Адаптер инкапсулирует ВСЮ обвязку записи: вызов API, инвалидацию кэша и
 * исход (`onSaved`/`onClose`/`onDeleted`) — ядро лишь вызывает `create`/
 * `replace`/`remove` на сабмите/удалении. PIN-подпись и финал дня (`sign`/
 * `approve`) в этот контракт НЕ входят — они принадлежат дневной оболочке
 * (`FieldSignPage` / `EntryDock` / `AdminTimesheetBackfillPage`), не редактору.
 */
export interface EntryEditorAdapter {
  create: (payload: TimesheetEntryPayload) => void;
  replace: (entryId: number, payload: TimesheetEntryPayload) => void;
  remove: (entryId: number) => Promise<void>;
  isSaving: boolean;
  isDeleting: boolean;
  saveError?: FormValidationIssue | null;
}

export interface EntryEditorCoreProps {
  mode: "create" | "edit";
  /** Обязательно в edit-mode. */
  initialEntry?: TimesheetEntry;
  recentProjects: readonly Project[];
  myWorkTypes: readonly WorkType[];
  /** Мутатор записи (self / admin). */
  adapter: EntryEditorAdapter;
  /** Запрос на закрытие редактора (× / ESC / отмена) — оболочка решает что делать. */
  onClose: () => void;
  /** Поверхность рендера. По умолчанию `mobile` (путь рабочего `/field`). */
  surface?: EntrySurface;
}
